import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Select } from '@wartek-id/select'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'

import { DUKUNGAN_UPAYA_TINDAK_LANJUT } from '../constants/upayaTindakLanjut'

import type { IFollowUpDocumentForm } from 'types/kinerja-pelaksanaan'

interface SelectSupportNeedProps {
  isDisabled?: boolean
  isReadOnly?: boolean
  isRequired?: boolean
  fieldName: 'additionalSupportNeed' | 'supportNeed'
}

export function SelectSupportNeed({
  isDisabled = false,
  isReadOnly = false,
  isRequired = false,
  fieldName,
}: SelectSupportNeedProps) {
  const [showOthersInput, setShowOthersInput] = useState(false)
  const formContext = useFormContext<IFollowUpDocumentForm>()

  if (!formContext) {
    throw new Error('SelectSupportNeed must be used within `<FormProvider />`')
  }

  const { control } = formContext

  return (
    <Controller
      control={control}
      name={fieldName}
      rules={{
        required: isRequired,
        maxLength: 250,
      }}
      render={({ field: { onChange, value } }) => (
        <fieldset className="space-y-2">
          <Text variant="heading-sm">
            Apa dukungan yang Anda butuhkan untuk melakukan upaya tindak lanjut?
          </Text>
          <div className="space-y-1">
            <Text
              as="label"
              htmlFor={`${fieldName}Dropdown`}
              color="subdued"
              aria-hidden={isReadOnly}
            >
              Kebutuhan Dukungan (Apa/Siapa)
            </Text>
            {isReadOnly ? (
              <>
                <Text className="overflow-wrap-anywhere">{value}</Text>
                <span className="sr-only">
                  Kebutuhan Dukungan (Apa/Siapa), {value}
                </span>
              </>
            ) : (
              <div className="max-w-full lg:w-4/6">
                <Select
                  id={`${fieldName}Dropdown`}
                  placeholder="Pilih di sini"
                  className="block"
                  isDisabled={isDisabled}
                  onChange={(event) => {
                    if (event.target.value === 'others') {
                      onChange('')
                      setShowOthersInput(true)
                    } else {
                      setShowOthersInput(false)
                      onChange(event.target.value)
                    }
                  }}
                >
                  <option disabled className="text-disabled" selected value="">
                    Pilih di sini
                  </option>
                  {DUKUNGAN_UPAYA_TINDAK_LANJUT.map((item, i) => (
                    <option key={i} value={item} selected={item === value}>
                      {item}
                    </option>
                  ))}
                  <option
                    selected={
                      ![...DUKUNGAN_UPAYA_TINDAK_LANJUT, ''].includes(value) ||
                      showOthersInput
                    }
                    value="others"
                  >
                    Lainnya
                  </option>
                </Select>
              </div>
            )}
          </div>
          {!isReadOnly &&
            (showOthersInput ||
              ![...DUKUNGAN_UPAYA_TINDAK_LANJUT, ''].includes(value)) && (
              <div className="space-y-1 max-w-full lg:w-4/6">
                <Text
                  as="label"
                  htmlFor={`${fieldName}TextArea`}
                  color="subdued"
                >
                  Kebutuhan Dukungan (Apa/Siapa): Lainnya
                </Text>
                <Textarea
                  isDisabled={isDisabled}
                  name={`${fieldName}TextArea`}
                  value={value}
                  onChange={(event) => onChange(event.target.value)}
                  id={`${fieldName}TextArea`}
                  placeholder="Contoh: Penggunaan ruang kelas untuk praktik simulasi."
                  maxLength={250}
                />
              </div>
            )}
          <input type="hidden" name={fieldName} />
        </fieldset>
      )}
    />
  )
}
